// Canonical venue category values.
//
// MUST TRACK `CATEGORY_TYPES` in `api/src/lib/venue-sweep-types.ts` — that is the
// list the Google Places sweep actually stamps on new venues via
// `pickPrimaryCategory()`. This file drifted badly once (12 entries here vs 33
// distinct values in production), which meant newly-discovered venues rendered
// as raw slugs like `garden_center` in the category picker.
//
// `categoryOptions()` merges this list with the categories already present on
// the venues being edited, so a future addition that lands in the backend first
// still renders — it just shows up appended rather than in curated order.
export const VENUE_CATEGORIES = [
  // Food & drink
  'bar',
  'pub',
  'wine_bar',
  'restaurant',
  'cafe',
  'night_club',
  // Performing arts / music
  'performing_arts_theater',
  'movie_theater',
  'concert_hall',
  'philharmonic_hall',
  'opera_house',
  'amphitheatre',
  'auditorium',
  'dance_hall',
  'karaoke',
  // Visual arts
  'art_gallery',
  'art_studio',
  'museum',
  // Culture & heritage
  'cultural_landmark',
  'historical_place',
  'historical_landmark',
  'monument',
  'sculpture',
  // Community & cultural spaces
  'community_center',
  'cultural_center',
  'event_venue',
  'convention_center',
  // Parks, gardens & nature
  'park',
  'national_park',
  'state_park',
  'hiking_area',
  'botanical_garden',
  'garden',
  'nature_preserve',
  'wildlife_park',
  'wildlife_refuge',
  'visitor_center',
  'planetarium',
  // Craft, maker & market spaces
  'market',
  'farmers_market',
  'flea_market',
  'garden_center',
  'gift_shop',
  'jewelry_store',
  'florist',
  'book_store',
  // Civic
  'library',
  // Agriculture & family nature
  'farm',
  'zoo',
  'aquarium',
  // Wellness
  'yoga_studio',
] as const;

export type VenueCategory = typeof VENUE_CATEGORIES[number];

/**
 * Convert a raw category value (e.g. `wine_bar`, `garden_center`) into a
 * human-friendly title-cased label (e.g. "Wine Bar", "Garden Center").
 * Falls back to the input when nothing useful can be produced.
 */
export function formatCategory(raw?: string | null): string {
  if (!raw) return '';
  return raw
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

/** Sorted list of `{ value, label }` for use in `<select>` options. */
export function categoryOptions(extras: string[] = []): Array<{ value: string; label: string }> {
  const all = Array.from(new Set<string>([...VENUE_CATEGORIES, ...extras.filter(Boolean)]));
  return all
    .map((value) => ({ value, label: formatCategory(value) }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** Slug a free-form label (e.g. "Wine Bar!") to canonical snake_case. */
export function slugifyCategory(raw: string): string {
  return raw.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

/**
 * Read all categories for a venue. Always returns a (possibly empty) array.
 * Prefers the multi-value `categories` field; falls back to the legacy
 * single `category` string for venues that haven't been re-saved yet.
 */
export function getVenueCategories(v: { categories?: string[]; category?: string }): string[] {
  if (Array.isArray(v.categories) && v.categories.length) {
    return v.categories.map((s) => String(s).trim()).filter(Boolean);
  }
  return v.category ? [v.category] : [];
}

/** Render a list of category slugs as a human-friendly comma-joined label. */
export function formatCategoryList(cats: readonly string[]): string {
  return cats.map(formatCategory).filter(Boolean).join(', ');
}
